import React, { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Dialog.css'
import Icona from '../Icona/Icona';
import FlatIcon from '../Icona/FlatIcon';
import { PopButton, PopWell } from './PopParts';
import { fitPlateTitle } from './fitTitle';

//mantenuto per compatibilità: il colore del popup ora è unico
export const dialogColors = {
    default: "dialog-bg-color-def",
    color1: "dialog-bg-color-1"
}

//messaggio di testo: la prima riga è il titolo, le altre il dettaglio
const Message = ({ txt }) => {
    const [head, ...rest] = String(txt).split("\n");
    return (
        <PopWell className="pop-well-msg">
            <p className="pop-msg-head">{head}</p>
            {rest.length > 0 && <p className="pop-txt">{rest.join(" ")}</p>}
        </PopWell>
    );
}

//targa del titolo: una sola riga, rimpicciolita solo se il titolo non ci starebbe (vedi fitTitle.js)
const Plate = ({ title, icon, plate }) => {
    const plateRef = useRef(null);
    const textRef = useRef(null);

    useLayoutEffect(() => {
        let active = true;
        const fit = () => {
            if (active) fitPlateTitle(plateRef.current, textRef.current);
        };
        fit();

        //la larghezza del testo cambia con lo schermo e quando il font ha finito di caricarsi
        window.addEventListener("resize", fit);
        if (document.fonts) {
            document.fonts.addEventListener("loadingdone", fit);
            document.fonts.ready.then(fit);
        }
        return () => {
            active = false;
            window.removeEventListener("resize", fit);
            if (document.fonts) document.fonts.removeEventListener("loadingdone", fit);
        };
    }, [title, icon, plate]);

    return (
        <div ref={plateRef} className={"pop-plate pop-plate-" + plate + (icon ? " pop-plate-ico" : "")}>
            {icon && <span className="pop-plate-icon"><Icona kind={icon} size={56} /></span>}
            <span ref={textRef} className="pop-plate-txt">{title}</span>
        </div>
    );
}

/*
    Popup del gioco: cornice, targa del titolo sul bordo, contenuto in vasca
    e barra comandi. Viene disegnato fuori dal contenitore scalato della
    schermata, così le misure seguono lo schermo reale (PC / telefono).

    plate: colore della targa (pink, red, fire, blue, green, cyan, navy, banner)
    icon: icona gamificata nella targa
    size: default | compact | wide
    actions: bottoni aggiuntivi [{id, txt, color, icon, onClick, enabled}]
    onClose: mostra la X in alto a destra
*/
const Dialog = ({
    id, title, txt, children, icon, plate = "pink", subtitle, size, width, className = "",
    common, cancel, confirm, canConfirm, confirmDestructive,
    commonTxt = "Chiudi", cancelTxt = "Indietro", confirmTxt = "Conferma",
    commonIcon, cancelIcon, confirmIcon,
    actions = [], onClose,
}) => {
    const content = children !== undefined ? children : txt;
    const sizeClass = size || (width && parseInt(width) > 600 ? "wide" : "default");

    const buttons = [];
    if (common != null) {
        buttons.push(<PopButton key="close" id="close" color="blue" icon={commonIcon} onClick={common}>{commonTxt}</PopButton>);
    }
    if (cancel != null) {
        buttons.push(<PopButton key="cancel" id="cancel" color="blue" icon={cancelIcon} onClick={cancel}>{cancelTxt}</PopButton>);
    }
    actions.forEach((a) => {
        buttons.push(
            <PopButton key={a.id} id={a.id} color={a.color} icon={a.icon} iconEnd={a.iconEnd} gIcon={a.gIcon} enabled={a.enabled !== false} onClick={a.onClick}>
                {a.txt}
            </PopButton>
        );
    });
    if (confirm != null) {
        buttons.push(
            <PopButton
                key="confirm"
                id="confirm"
                color={confirmDestructive ? "red" : "green"}
                icon={confirmIcon}
                enabled={!!canConfirm}
                onClick={confirm}
            >
                {confirmTxt}
            </PopButton>
        );
    }

    const dialog = (
        <div className='dialog' id={id} style={{ opacity: 0 }}>
            <div className={"pop-shell pop-" + sizeClass + (title ? " pop-has-plate" : "") + (onClose ? " pop-has-close" : "") + " " + className} role="dialog" aria-modal="true">
                {title && <Plate title={title} icon={icon} plate={plate} />}

                {onClose && (
                    <button type="button" className="pop-close pop-g-blue pop-gloss" aria-label="Chiudi" onClick={onClose}>
                        <FlatIcon name="close" size={26} />
                    </button>
                )}

                {subtitle && <p className="pop-subtitle">{subtitle}</p>}

                <div className="pop-body">
                    {typeof content === "string" ? <Message txt={content} /> : content}
                </div>

                {buttons.length > 0 && (
                    <div className={"pop-actions pop-actions-" + buttons.length}>
                        {buttons}
                    </div>
                )}
            </div>
        </div>
    );

    //contenitore sempre uguale: se cambiasse tra un render e l'altro React ricreerebbe il popup
    return createPortal(dialog, document.body);
}

export default Dialog;
